(() => {
  const initialized = new WeakSet();
  const track = (name, data) => {
    try { window.HugfigAnalytics?.track?.(name, data); } catch (_) { /* Analytics cannot interrupt a purchase. */ }
  };
  const text = (tag, className, value) => {
    const element = document.createElement(tag);
    element.className = className;
    element.textContent = value;
    return element;
  };
  const safeImage = (value) => typeof value === 'string' && /^(https:|\/\/)/.test(value) ? value : '';
  const imageSource = (variant, product, allowFallback = true) => {
    const image = variant?.featured_image || (allowFallback && product.featured_image);
    return safeImage(image?.high || image?.src || image);
  };

  class ReadyMadeProduct {
    constructor(root) {
      this.root = root;
      this.data = JSON.parse(root.querySelector('[data-rm-data]').textContent);
      this.product = this.data.product;
      this.labels = this.data.labels;
      this.form = root.querySelector('form');
      this.variantInput = root.querySelector('[data-rm-variant]');
      this.options = root.querySelector('[data-rm-options]');
      this.cartButton = root.querySelector('[data-rm-cart]');
      this.buyButton = root.querySelector('[data-rm-buy]');
      this.status = root.querySelector('[data-rm-status]');
      this.image = root.querySelector('.hf-rm__image');
      this.zoom = root.querySelector('[data-rm-zoom]');
      this.lightbox = root.querySelector('[data-rm-lightbox]');
      this.sizeIndex = this.product.options.findIndex((name) => /\b(size|height)\b/i.test(name));
      this.designIndex = this.product.options.findIndex((name, index) => index !== this.sizeIndex && /style|design|type/i.test(name));
      if (this.designIndex < 0) this.designIndex = this.product.options.findIndex((_, index) => index !== this.sizeIndex);
      this.multipleDesigns = this.designIndex >= 0 && new Set(this.product.variants.map((variant) => variant.options[this.designIndex])).size > 1;
      const urlVariant = new URL(window.location.href).searchParams.get('variant');
      const initial = this.product.variants.find((variant) => String(variant.id) === String(urlVariant))
        || this.product.variants.find((variant) => String(variant.id) === String(this.data.initialVariant))
        || this.product.variants.find((variant) => variant.available) || this.product.variants[0];
      if (!initial) return;
      this.selection = [...initial.options];
      this.moneyFormatter = new Intl.NumberFormat(this.data.locale || 'en', { style: 'currency', currency: this.data.currency || 'USD', currencyDisplay: 'narrowSymbol' });
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.begin('cart', event.submitter || this.cartButton); });
      this.buyButton.addEventListener('click', () => this.begin('checkout', this.buyButton));
      this.zoom.addEventListener('click', () => {
        if (!this.image?.src || this.image.hidden) return;
        const enlarged = root.querySelector('[data-rm-zoom-image]');
        enlarged.src = this.image.src;
        enlarged.alt = this.image.alt;
        this.lockZoomScroll();
        this.lightbox.showModal();
      });
      root.querySelector('[data-rm-zoom-close]').addEventListener('click', () => this.lightbox.close());
      this.lightbox.addEventListener('click', (event) => { if (event.target === this.lightbox) this.lightbox.close(); });
      this.lightbox.addEventListener('close', () => {
        this.unlockZoomScroll();
        this.zoom.focus({ preventScroll: true });
      });
      this.image?.addEventListener('error', () => this.setImageError());
      this.image?.addEventListener('load', () => this.imageLoaded());
      this.renderOptions();
      root.querySelector('[data-rm-fallback]').hidden = true;
      this.options.hidden = false;
      this.buyButton.hidden = false;
      root.classList.add('is-enhanced');
      this.update(false);
    }

    money(cents) { return this.moneyFormatter.format(Number(cents) / 100); }

    matching(selection) {
      return this.product.variants.find((variant) => variant.options.every((option, index) => option === selection[index]));
    }

    renderOptions() {
      this.product.options.forEach((name, index) => {
        const values = [...new Set(this.product.variants.map((variant) => variant.options[index]))];
        if (values.length < 2) return;
        const group = text('fieldset', 'hf-rm__group', '');
        const label = index === this.sizeIndex ? this.labels.height : index === this.designIndex ? this.labels.design : name;
        group.append(text('legend', 'hf-rm__legend', label));
        const choices = text('div', `hf-rm__choices${index === this.sizeIndex ? ' hf-rm__choices--size' : ''}`, '');
        for (const value of values) {
          const button = text('button', 'hf-rm__choice', '');
          button.type = 'button';
          button.dataset.optionIndex = index;
          button.dataset.optionValue = value;
          if (index === this.designIndex) {
            const source = imageSource(this.product.variants.find((variant) => variant.options[index] === value && variant.featured_image), this.product, !this.multipleDesigns);
            if (source) {
              const image = document.createElement('img');
              image.src = source; image.alt = ''; image.width = 46; image.height = 54; image.loading = 'lazy';
              image.addEventListener('error', () => image.remove(), { once: true });
              button.append(image);
            }
          }
          let labelValue = value;
          const size = value.match(/^(\d+(?:\.\d+)?)\s*cm$/i);
          if (index === this.sizeIndex && size) labelValue += ` (${(Number(size[1]) / 2.54).toFixed(1)} in)`;
          button.append(text('span', 'hf-rm__choice-label', labelValue));
          if (index === this.sizeIndex) button.append(text('span', 'hf-rm__choice-price', ''));
          button.addEventListener('click', () => {
            if (this.busy || this.uncertain) return;
            this.selection[index] = value;
            // A design change preserves height when that actual combination exists.
            // It never silently purchases a different size when the combination is absent.
            this.update(true);
          });
          choices.append(button);
        }
        group.append(choices);
        this.options.append(group);
      });
    }

    update(updateURL) {
      this.variant = this.matching(this.selection);
      this.variantInput.value = this.variant ? String(this.variant.id) : '';
      for (const button of this.options.querySelectorAll('[data-option-index]')) {
        const index = Number(button.dataset.optionIndex);
        const selected = [...this.selection]; selected[index] = button.dataset.optionValue;
        const candidate = this.matching(selected);
        const available = index === this.designIndex
          ? this.product.variants.some((variant) => variant.available && variant.options[index] === button.dataset.optionValue)
          : Boolean(candidate?.available);
        button.disabled = !available || Boolean(this.busy || this.uncertain);
        button.setAttribute('aria-pressed', String(this.selection[index] === button.dataset.optionValue));
        const price = button.querySelector('.hf-rm__choice-price');
        if (price) price.textContent = candidate?.available ? this.money(candidate.price) : this.labels.soldOut;
      }
      this.root.querySelector('[data-rm-price]').textContent = this.variant ? this.money(this.variant.price) : '—';
      this.root.querySelector('[data-rm-summary]').textContent = this.selection.filter((value) => value !== 'Default Title').join(' · ');
      const available = Boolean(this.variant?.available);
      this.cartButton.disabled = !available || Boolean(this.busy || this.uncertain);
      this.buyButton.disabled = !available || Boolean(this.busy || this.uncertain);
      if (!this.busy && !this.uncertain) {
        this.cartButton.textContent = available ? this.labels.cart : this.labels.soldOut;
        this.buyButton.textContent = available ? this.labels.buy : this.labels.soldOut;
        this.message(available ? '' : this.labels.missing);
      }
      // Prefer this design's own media when an unavailable combination has no variant.
      const mediaVariant = this.variant?.featured_image ? this.variant : this.product.variants.find((variant) => variant.options[this.designIndex] === this.selection[this.designIndex] && variant.featured_image);
      const source = imageSource(mediaVariant, this.product, !this.multipleDesigns);
      this.expectedImage = source;
      if (!this.image && source) {
        this.image = document.createElement('img');
        this.image.className = 'hf-rm__image'; this.image.width = 900; this.image.height = 900;
        this.image.addEventListener('error', () => this.setImageError());
        this.image.addEventListener('load', () => this.imageLoaded());
        this.root.querySelector('.hf-rm__media').prepend(this.image);
      }
      if (this.image && source) {
        this.image.alt = `${this.product.title} — ${this.selection[this.designIndex] || ''}`;
        if (this.image.getAttribute('src') !== source) {
          this.image.removeAttribute('srcset');
          this.image.src = source;
        }
        if (this.image.complete && this.image.naturalWidth) { this.image.hidden = false; this.zoom.hidden = false; }
      } else this.setImageError();
      this.renderThumbnails(source);
      if (updateURL) {
        const url = new URL(window.location.href);
        if (this.variant) url.searchParams.set('variant', this.variant.id); else url.searchParams.delete('variant');
        window.history.replaceState({}, '', url);
      }
    }

    renderThumbnails(primary) {
      const container = this.root.querySelector('[data-rm-thumbnails]');
      if (!container) return;
      // Additional product photos are browsing-only; they never choose a variant.
      const details = (this.data.media || []).filter((media) => !media.variantIds?.some(Boolean) && safeImage(media.src) && media.src.split('?')[0] !== primary.split('?')[0]);
      container.replaceChildren();
      container.hidden = !details.length || !primary;
      if (container.hidden) return;
      const media = [{ src: primary, alt: this.product.title }, ...details];
      media.forEach((item, index) => {
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'hf-rm__thumbnail';
        button.setAttribute('aria-label', `${this.product.title} — ${index + 1}`);
        button.setAttribute('aria-pressed', String(index === 0));
        const image = document.createElement('img');
        image.src = item.src; image.alt = ''; image.width = 64; image.height = 64; image.loading = 'lazy';
        button.append(image);
        button.addEventListener('click', () => {
          for (const sibling of container.children) sibling.setAttribute('aria-pressed', String(sibling === button));
          this.expectedImage = item.src;
          this.image.src = item.src; this.image.alt = item.alt || this.product.title;
        });
        container.append(button);
      });
    }

    setImageError() {
      this.expectedImage = '';
      if (this.image) {
        this.image.hidden = true;
        this.image.removeAttribute('srcset');
        this.image.removeAttribute('src');
      }
      this.zoom.hidden = true;
      this.root.querySelector('[data-rm-image-error]').hidden = false;
    }

    imageLoaded() {
      if (!this.expectedImage || !this.image?.naturalWidth || this.image.src !== new URL(this.expectedImage, window.location.href).href) return;
      this.image.hidden = false;
      this.zoom.hidden = false;
      this.root.querySelector('[data-rm-image-error]').hidden = true;
    }

    lockZoomScroll() {
      if (document.body.classList.contains('hf-rb-dialog-open')) return;
      this.zoomScroll = window.scrollY;
      this.previousBodyTop = document.body.style.top;
      this.ownsZoomLock = true;
      document.documentElement.classList.add('hf-rb-dialog-open');
      document.body.classList.add('hf-rb-dialog-open');
      document.body.style.top = `-${this.zoomScroll}px`;
    }

    unlockZoomScroll() {
      if (!this.ownsZoomLock) return;
      this.ownsZoomLock = false;
      document.documentElement.classList.remove('hf-rb-dialog-open');
      document.body.classList.remove('hf-rb-dialog-open');
      document.body.style.top = this.previousBodyTop;
      window.scrollTo(0, this.zoomScroll);
    }

    message(value, cartLink = false) {
      this.status.replaceChildren(document.createTextNode(value));
      this.status.hidden = !value;
      if (cartLink) {
        const link = text('a', '', ` ${this.labels.viewCart}`);
        link.href = `${window.Shopify?.routes?.root || '/'}cart`;
        this.status.append(link);
      }
    }

    async begin(destination, trigger) {
      if (this.busy || this.uncertain || !this.variant?.available) return;
      this.busy = true;
      this.message('');
      this.update(false);
      const variant = this.variant;
      let result = { outcome: 'skipped', addon: null };
      let previousStyle = 'none';
      let cartSubmissionStarted = false;
      const isKeychain = this.data.productType === 'keychain';
      try {
        if (this.data.productType && !window.HugfigAddonDialog) throw new Error('addon_not_ready');
        if (this.data.productType && window.HugfigAddonDialog) {
          result = await window.HugfigAddonDialog.open({
            productType: this.data.productType,
            trigger,
            track: (name, eventData, meta) => {
              const payload = { ...eventData, journey_type: 'standard_product' };
              if (name === 'keychain_addon_view') {
                if (this.viewedAddon) return;
                this.viewedAddon = true;
                payload.available_keychain_styles = meta.availableStyles;
              }
              if (name === 'keychain_addon_select') {
                previousStyle = eventData.keychain_style;
                payload.price = Number(eventData.price) / 100;
                payload.variant_id = String(eventData.variant_id || '');
              }
              track(name, payload);
            }
          });
        }
        if (result.outcome === 'cancelled') return;
        if (isKeychain && window.HugfigAddonDialog) {
          if (result.addon) track('keychain_addon_confirm', { journey_type: 'standard_product', destination, placement: 'modal', keychain_style: result.addon.title, variant_id: String(result.addon.id), price: Number(result.addon.price) / 100 });
          else track('keychain_addon_skip', { journey_type: 'standard_product', destination, placement: 'modal', previous_keychain_style: previousStyle });
        }
        cartSubmissionStarted = true;
        await this.submit(variant, result.addon, destination, trigger);
      } catch (error) {
        if (isKeychain && cartSubmissionStarted) track('keychain_addon_error', { journey_type: 'standard_product', destination, placement: 'modal', keychain_style: result.addon?.title || 'none', error_code: 'cart_add_failed' });
        this.pendingError = error.uncertain ? this.labels.uncertain : this.labels.error;
        this.uncertain = Boolean(error.uncertain);
      } finally {
        this.busy = false;
        this.update(false);
        if (result.outcome === 'cancelled') trigger?.focus({ preventScroll: true });
        if (this.pendingError) {
          this.message(this.pendingError, this.uncertain);
          this.pendingError = '';
          this.status.scrollIntoView({ block: 'center', behavior: 'auto' });
        }
      }
    }

    async submit(variant, addon, destination, trigger) {
      this.cartButton.textContent = this.labels.loading;
      this.buyButton.textContent = this.labels.loading;
      const routeRoot = window.Shopify?.routes?.root || '/';
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      // Match the existing standard-product cart contract, including automatic
      // merging of repeat selections. Ready-made does not create an AI session.
      const properties = { '_Selected Variant ID': String(variant.id) };
      if (this.sizeIndex >= 0) properties.Size = variant.options[this.sizeIndex];
      if (addon?.type === 'keychain') properties.Keychain = addon.title;
      const items = [{ id: variant.id, quantity: 1, properties }];
      if (addon) items.push({ id: addon.id, quantity: 1, properties: { '_HugFig Add-on Type': addon.type } });
      const payload = { items };
      if (cart?.getSectionsToRender) {
        payload.sections = cart.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
        cart.setActiveElement?.(trigger);
      }
      let response, data;
      try {
        response = await fetch(`${routeRoot}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) });
        data = await response.json();
      } catch (_) {
        // A lost response may follow a successful write. Do not blindly submit again.
        const error = new Error('unknown_cart_result'); error.uncertain = true; throw error;
      }
      if (!response.ok || data.status) throw new Error('cart_add_failed');
      track('add_to_cart_context', { journey_type: 'standard_product', has_generated_preview: false, variant_id: String(variant.id), quantity: 1 });
      if (destination === 'checkout') {
        track('checkout_handoff', { journey_type: 'standard_product', has_generated_preview: false, variant_id: String(variant.id), quantity: 1 });
        window.location.assign(`${routeRoot}checkout`);
        return;
      }
      if (cart?.renderContents) {
        try {
          if (data.items?.[0]) Object.assign(data, data.items[0]);
          cart.classList.remove('is-empty');
          cart.renderContents(data);
          return;
        } catch (_) { /* The write succeeded; use the existing cart page as fallback. */ }
      }
      window.location.assign(`${routeRoot}cart`);
    }
  }

  const init = (scope = document) => {
    const roots = [...scope.querySelectorAll('[data-hf-ready-made]')];
    if (scope.matches?.('[data-hf-ready-made]')) roots.unshift(scope);
    for (const root of roots) {
      if (initialized.has(root)) continue;
      try { new ReadyMadeProduct(root); initialized.add(root); } catch (error) { console.error('Ready-made options could not initialize.', error); }
    }
  };
  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
